import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Clock, CreditCard, Crown, Calendar, AlertCircle, User } from 'lucide-react';
import { Account, Subscription } from '../types';
import { api } from '../services/api';


interface SettingsPageProps {
  onBack: () => void;
  account: Account;
  onAccountUpdate: (account: Account) => void;
  onUpgrade?: () => void;
  onOpenSupport?: () => void;
}

const THEMES = ["light", "dark", "corporate", "business", "dim", "emerald", "synthwave", "cupcake", "pastel", "valentine", "acid", "winter", "autumn", "fantasy"] as const;
type Theme = typeof THEMES[number];

export const SettingsPage: React.FC<SettingsPageProps> = ({ 
  onBack, 
  account, 
  onAccountUpdate,
  onUpgrade
}) => {
  const [currentTheme, _setCurrentTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem("theme") as Theme;
    return saved && THEMES.includes(saved) ? saved : "light";
  });
  
  const [dataRetentionDays, setDataRetentionDays] = useState<number>(() => {
    if (account.settings?.dataRetentionDays) {
      const days = parseInt(account.settings.dataRetentionDays.toString());
      return isNaN(days) ? 30 : days;
    }
    return 30;
  });

  // Outbound email settings
  const [emailFromAddress] = useState<string>(() => {
    return account.settings?.['Email.FromAddress'] ?? '';
  });
  const [emailFromName, _setEmailFromName] = useState<string>(() => {
    return account.settings?.['Email.FromName'] ?? '';
  });
  const [emailPatientSummarySubject, _setEmailPatientSummarySubject] = useState<string>(() => {
    return account.settings?.['Email.Subjects.PatientSummary'] ?? 'Your visit summary';
  });
  
  // Communication settings
  const [clinicianName, setClinicianName] = useState<string>(() => {
    return account.settings?.['Communication.ClinicianName'] ?? '';
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  // Update theme in localStorage and document when it changes
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", currentTheme);
    localStorage.setItem("theme", currentTheme);
  }, [currentTheme]);

  // Load subscription data
  useEffect(() => {
    if (account?.id) {
      setIsLoadingSubscription(true);
      api.getCurrentSubscription(account.id)
        .then((result) => {
          if ('status' in result && result.status === 'none') {
            setSubscription(null);
          } else {
            setSubscription(result as Subscription);
          }
        })
        .catch((error) => {
          console.error('Failed to load subscription:', error);
          setSubscription(null);
        })
        .finally(() => {
          setIsLoadingSubscription(false);
        });
    }
  }, [account?.id]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Prepare settings to save
      const settings: Record<string, string> = {
        theme: currentTheme,
        dataRetentionDays: dataRetentionDays.toString(),
        'Email.FromAddress': emailFromAddress ?? '',
        'Email.FromName': emailFromName ?? '',
        'Email.Subjects.PatientSummary': emailPatientSummarySubject ?? '',
        'Communication.ClinicianName': clinicianName ?? '',
      };
      
      // Save to backend
      const savedAccount = await api.updateAccountSettings(account.id, settings);
      
      // Update local state
      onAccountUpdate(savedAccount);
      
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageBilling = async () => {
    if (!account?.id) return;
    
    try {
      const returnUrl = window.location.href;
      const response = await api.createPortalSession({
        accountId: account.id,
        returnUrl
      });
      
      window.location.href = response.url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const formatDate = (value: string | Date | null | undefined) => {
    console.log('date value edited', value)
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(d);
  };

  const getSubscriptionStatusText = () => {
    if (!subscription) return 'Free Plan';
    
    switch (subscription.status) {
      case 'active':
        return 'Active Subscription';
      case 'trialing':
        return 'Trial Period';
      case 'canceled':
        return 'Cancelled';
      case 'past_due':
        return 'Past Due';
      case 'incomplete':
        return 'Payment Required';
      default:
        return 'Free Plan';
    }
  };

  const getStatusBadgeClass = () => {
    if (!subscription) return 'badge-info text-white';
    
    switch (subscription.status) {
      case 'active':
      case 'trialing':
        return 'badge-success';
      case 'past_due':
      case 'incomplete':
        return 'badge-error';
      case 'canceled':
        return 'badge-warning';
      default:
        return 'badge-info';
    }
  };

  const isSubscriptionActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  return (
    <div className="settings-page min-h-screen bg-base-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="btn btn-ghost btn-circle"
            aria-label="Back"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-base-content">Settings</h1>
        </div>

        {/* Settings Form */}
        <div className="space-y-8">
          {/* Billing Section */}
          <div className="bg-base-200 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <CreditCard size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-base-content">Billing & Subscription</h2>
            </div>

            {isLoadingSubscription ? (
              <div className="flex items-center justify-center p-8">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current Plan */}
                <div className="flex items-center justify-between p-4 bg-base-100 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-base-content">Current Plan</h3>
                    <p className="text-base-content/70 text-sm">{getSubscriptionStatusText()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`badge ${getStatusBadgeClass()}`}>
                      {getSubscriptionStatusText()}
                    </span>
                  </div>
                </div>

                {/* Subscription Details */}
                {subscription && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {subscription.currentPeriodEnd && (
                      <div className="flex items-center gap-3 p-3 bg-base-100 rounded-lg">
                        <Calendar size={20} className="text-primary" />
                        <div>
                          <p className="text-sm font-medium text-base-content">
                            {subscription.cancelAtPeriodEnd ? 'Expires' : 'Renews'}
                          </p>
                          <p className="text-xs text-base-content/70">
                            {formatDate(subscription.currentPeriodEnd)}
                          </p>
                        </div>
                      </div>
                    )}

                    {subscription.cancelAtPeriodEnd && (
                      <div className="flex items-center gap-3 p-3 bg-warning/10 rounded-lg border border-warning/20">
                        <AlertCircle size={20} className="text-warning" />
                        <div>
                          <p className="text-sm font-medium text-warning">Cancellation Scheduled</p>
                          <p className="text-xs text-warning/70">Access until period end</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  {isSubscriptionActive ? (
                    <button
                      onClick={handleManageBilling}
                      className="btn btn-primary"
                    >
                      <CreditCard size={20} />
                      Manage Billing
                    </button>
                  ) : (
                    <button
                      onClick={onUpgrade}
                      className="btn btn-primary"
                    >
                      <Crown size={20} />
                      Upgrade Plan
                    </button>
                  )}

                  {!isSubscriptionActive && subscription?.cancelAtPeriodEnd && (
                    <button
                      onClick={handleManageBilling}
                      className="btn btn-outline"
                    >
                      Reactivate Subscription
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Application Settings */}
          <div className="bg-base-200 rounded-lg p-6 shadow-lg">

          {/* Data Retention Settings */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Clock size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-base-content">Data Retention</h2>
            </div>
            <p className="text-base-content/70 mb-4">
              Control how long your data is retained before automatic deletion.
            </p>
            <div className="form-control w-full max-w-xs">
              <label className="label">
                <span className="label-text">Retention Period (days)</span>
                <span className="label-text-alt">Default: 30 days</span>
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={dataRetentionDays}
                onChange={(e) => setDataRetentionDays(parseInt(e.target.value) || 30)}
                className="input input-bordered w-full"
                placeholder="30"
              />
              <label className="label">
                <span className="label-text-alt">
                  Data will be automatically deleted after {dataRetentionDays} days
                </span>
              </label>
            </div>
          </div>

          {/* Communication Settings */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <User size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-base-content">Communication Settings</h2>
            </div>
            <p className="text-base-content/70 mb-4">
              Configure your name or practice name for patient communications and notes.
            </p>
            <div className="form-control w-full max-w-md">
              <label className="label">
                <span className="label-text">Clinician or Practice Name</span>
                <span className="label-text-alt">Used in patient communications</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={clinicianName}
                onChange={(e) => setClinicianName(e.target.value)}
                placeholder="Dr. Jane Smith, MD or Smith Medical Practice"
              />
              <label className="label">
                <span className="label-text-alt">
                  This name will replace placeholders like [Clinician's Name and Credentials] in patient summaries
                </span>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-primary"
            >
              {isSaving ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Save Settings
                </>
              )}
            </button>
            
            {saveMessage && (
              <div className={`alert ${saveMessage.includes('successfully') ? 'alert-success' : 'alert-error'}`}>
                <span>{saveMessage}</span>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}; 